import * as React from 'react';
import { Box, Button, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { useParams, useNavigate } from 'react-router';
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import ImagePicker from '../components/ImagePicker';
import { RichTextEditor } from '../components/RichTextEditor';
import { sanitizeTelegramHtml } from '../utils';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

function useDebounce(value: string, delay = 400) {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

const checkboxStyle = `
  .purple-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1.5px solid #e0d7f5;
    background: #faf8ff;
    transition: border-color 0.15s, background 0.15s;
    font-size: 14px;
    color: #3d2a6e;
    user-select: none;
  }
  .purple-checkbox:hover {
    border-color: #7c3aed;
    background: #f3eeff;
  }
  .purple-checkbox input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 17px;
    height: 17px;
    border: 2px solid #a78bfa;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    transition: background 0.15s, border-color 0.15s;
  }
  .purple-checkbox input[type="checkbox"]:checked {
    background: #7c3aed;
    border-color: #7c3aed;
  }
  .purple-checkbox input[type="checkbox"]:checked::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 0px;
    width: 5px;
    height: 9px;
    border: 2px solid #fff;
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
  }
  .purple-checkbox.checked {
    border-color: #7c3aed;
    background: #f3eeff;
  }
`;

export default function BroadcastEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [broadcast, setBroadcast] = React.useState<any>(null);

  const [regions, setRegions] = React.useState<any[]>([]);
  const [cities, setCities] = React.useState<any[]>([]);

  const [selectedRegions, setSelectedRegions] = React.useState<number[]>([]);
  const [selectedCities, setSelectedCities] = React.useState<number[]>([]);

  const [allRegions, setAllRegions] = React.useState(false);
  const [allCities, setAllCities] = React.useState(false);

  // ✅ geoAll: send to everyone regardless of region/city
  const [geoAll, setGeoAll] = React.useState(false);

  const [regionSearch, setRegionSearch] = React.useState('');
  const [citySearch, setCitySearch] = React.useState('');

  const debouncedRegionSearch = useDebounce(regionSearch);
  const debouncedCitySearch = useDebounce(citySearch);

  const [image, setImage] = React.useState<File | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [mediaId, setMediaId] = React.useState<number | null>(null);

  const [scheduledDate, setScheduledDate] = React.useState<Dayjs | null>(dayjs());
  const [scheduledTime, setScheduledTime] = React.useState<Dayjs | null>(dayjs());

  React.useEffect(() => {
    fetch(`${API_URL}/broadcast/${id}`)
      .then((r) => r.json())
      .then(setBroadcast);
  }, [id]);

  React.useEffect(() => {
    if (allRegions || geoAll) return;

    fetch(`${API_URL}/region?search=${debouncedRegionSearch}`)
      .then((r) => r.json())
      .then((r) => setRegions(r.data));
  }, [debouncedRegionSearch, allRegions, geoAll]);

  React.useEffect(() => {
    if (geoAll) {
      setCities([]);
      return;
    }

    if (!selectedRegions.length && !allRegions) {
      setCities([]);
      return;
    }

    if (allCities && !allRegions) {
      // Fetch all cities (limit 30) for the selected regions
      fetch(
        `${API_URL}/region-cities?search=&regionIds=${selectedRegions.join(',')}&limit=30`,
      )
        .then((r) => r.json())
        .then((r) => setCities(r.data));
      return;
    }

    if (allRegions || allCities) {
      setCities([]);
      return;
    }

    // ✅ Fix 3: Always fetch for ALL selected regions so previously-checked
    // cities remain visible (and checked) when a new region is added
    fetch(
      `${API_URL}/region-cities?search=${debouncedCitySearch}&regionIds=${selectedRegions.join(',')}&limit=30`,
    )
      .then((r) => r.json())
      .then((r) => setCities(r.data));
  }, [selectedRegions, debouncedCitySearch, allRegions, allCities, geoAll]);

  React.useEffect(() => {
    if (!broadcast) return;

    // ✅ Read geoAll from backend
    setGeoAll(broadcast.geoAll === true);

    if (broadcast.broadcastRegions?.length) {
      setSelectedRegions(broadcast.broadcastRegions.map((r: any) => r.regionId));
      setAllRegions(false);
    } else if (broadcast.allRegions === true) {
      setAllRegions(true);
    }

    if (broadcast.broadcastCities?.length) {
      setSelectedCities(broadcast.broadcastCities.map((c: any) => c.citiesId));
      setAllCities(false);
    } else if (broadcast.allCities === true) {
      setAllCities(true);
    }

    setScheduledDate(
      broadcast.scheduledDateAt
        ? dayjs(broadcast.scheduledDateAt, 'DD.MM.YYYY')
        : null,
    );

    setScheduledTime(
      broadcast.scheduledTimeAt
        ? dayjs(broadcast.scheduledTimeAt, 'HH:mm')
        : null,
    );

    if (broadcast.broadcastMedia) {
      if (broadcast.broadcastMedia.imageUrl) {
        setImageUrl(`${IMAGE_URL}/${broadcast.broadcastMedia.imageUrl}`);
      }
      if (broadcast.broadcastMedia.mediaId) {
        setMediaId(broadcast.broadcastMedia.mediaId);
      }
    }
  }, [broadcast]);

  if (!broadcast) return null;

  const save = () => {
    const formData = new FormData();

    formData.append('message', sanitizeTelegramHtml(broadcast.message));
    formData.append('geoAll', String(geoAll));
    formData.append(
      'regionIds',
      geoAll ? '' : allRegions ? 'all' : selectedRegions.join(','),
    );
    formData.append(
      'cityIds',
      geoAll ? '' : allCities ? 'all' : selectedCities.join(','),
    );
    formData.append(
      'scheduledDateAt',
      scheduledDate ? scheduledDate.format('DD.MM.YYYY') : '',
    );
    formData.append(
      'scheduledTimeAt',
      scheduledTime ? scheduledTime.format('HH:mm') : '',
    );
    formData.append('channelType', broadcast.channelType ?? 'all');
    formData.append('auditory', broadcast.auditory ?? 'all');

    if (image) {
      formData.append('image', image);
    } else if (mediaId) {
      formData.append('mediaId', String(mediaId));
    }

    fetch(`${API_URL}/broadcast/${id}`, {
      method: 'PUT',
      body: formData,
    }).then(() => navigate('/broadcasts'));
  };

  const showGeoSelector = !geoAll;
  const showCities =
    showGeoSelector && (allRegions || selectedRegions.length > 0 || allCities);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <style>{checkboxStyle}</style>

      <Box sx={{ maxWidth: 900 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Редагування розсилки
        </Typography>

        <RichTextEditor
          value={broadcast.message}
          onChange={(html) => setBroadcast({ ...broadcast, message: html })}
        />

        {/* ✅ geoAll toggle */}
        <Box
          sx={{
            my: 3,
            py: 0.5,
            px: 2,
            borderRadius: 2,
            border: '1.5px solid',
            borderColor: geoAll ? '#7c3aed' : '#e0d7f5',
            background: geoAll ? '#f3eeff' : '#faf8ff',
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={geoAll}
                onChange={(e) => setGeoAll(e.target.checked)}
                sx={{
                  color: '#a78bfa',
                  '&.Mui-checked': { color: '#7c3aed' },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: 14,
                  color: geoAll ? '#5b21b6' : '#3d2a6e',
                  fontWeight: geoAll ? 600 : 400,
                }}
              >
                Розсилати повідомлення всім користувачам незалежно від області або міста
              </Typography>
            }
          />
        </Box>

        {/* ——— Regions ——— */}
        {showGeoSelector && (
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mb: 1 }}>Області</Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {allRegions ? (
                <Box
                  sx={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1.5px solid #7c3aed',
                    background: '#f3eeff',
                    color: '#7c3aed',
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                >
                  Обрані всі області
                </Box>
              ) : (
                <input
                  placeholder="Пошук області..."
                  value={regionSearch}
                  onChange={(e) => setRegionSearch(e.target.value)}
                  style={{
                    flex: 1,
                    padding: 8,
                    borderRadius: 6,
                    border: '1.5px solid #d1c4e9',
                    outline: 'none',
                  }}
                />
              )}

              <Button
                variant="outlined"
                sx={{ borderColor: '#9ca3af', color: '#6b7280' }}
                onClick={() => {
                  setAllRegions(false);
                  setSelectedRegions([]);
                  setSelectedCities([]);
                  setAllCities(false);
                  setRegionSearch('');
                }}
              >
                Скасувати
              </Button>
            </Box>

            {!allRegions && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                }}
              >
                {regions.map((r) => {
                  const checked = selectedRegions.includes(r.regionId);
                  return (
                    <label
                      key={r.regionId}
                      className={`purple-checkbox${checked ? ' checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (checked) {
                            setSelectedRegions(
                              selectedRegions.filter((rid) => rid !== r.regionId),
                            );
                            // Keep selectedCities — invalid ones will disappear
                            // naturally when city list re-fetches for remaining regions
                          } else {
                            setSelectedRegions([...selectedRegions, r.regionId]);
                            // Don't reset selectedCities so existing picks are kept
                          }
                          setAllCities(false);
                        }}
                      />
                      {r.regionName}
                    </label>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {showCities && <Box sx={{ my: 2, fontSize: 14, color: 'text.secondary' }}>
          Якщо для обраної області не вказано місто, повідомлення буде надіслано до всіх міст цієї області.
        </Box> }

        {/* ——— Cities ——— */}
        {showCities && (
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mb: 1 }}>Міста</Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {allCities ? (
                <Box
                  sx={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1.5px solid #7c3aed',
                    background: '#f3eeff',
                    color: '#7c3aed',
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                >
                  Обрані всі міста в областях
                </Box>
              ) : (
                <input
                  placeholder="Пошук міста..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  style={{
                    flex: 1,
                    padding: 8,
                    borderRadius: 6,
                    border: '1.5px solid #d1c4e9',
                    outline: 'none',
                  }}
                />
              )}

              <Button
                variant="outlined"
                sx={{ borderColor: '#9ca3af', color: '#6b7280' }}
                onClick={() => {
                  setAllCities(false);
                  setSelectedCities([]);
                  setCitySearch('');
                }}
              >
                Скасувати
              </Button>
            </Box>

            {!allCities && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                }}
              >
                {cities.map((c) => {
                  const checked = selectedCities.includes(c.citiesId);
                  return (
                    <label
                      key={c.citiesId}
                      className={`purple-checkbox${checked ? ' checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (checked) {
                            setSelectedCities(
                              selectedCities.filter((cid) => cid !== c.citiesId),
                            );
                          } else {
                            setSelectedCities([...selectedCities, c.citiesId]);
                          }
                        }}
                      />
                      {c.name}
                    </label>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* ——— Date & Time pickers ——— */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <DatePicker
            label="Дата"
            value={scheduledDate}
            onChange={setScheduledDate}
            format="DD.MM.YYYY"
          />
          <TimePicker
            label="Час"
            value={scheduledTime}
            onChange={setScheduledTime}
            ampm={false}
          />
        </Box>

        <ImagePicker
          title="Зображення"
          value={imageUrl || ''}
          onChange={(value) => {
            if (value.type === 'file') {
              setImage(value.file);
              setMediaId(null);
              setImageUrl(URL.createObjectURL(value.file));
            }

            if (value.type === 'media') {
              setImage(null);
              setMediaId(value.mediaId);
              setImageUrl(value.url);
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={save}>
            Зберегти
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Скасувати
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}